// 可能組合方式數（ways-to-win / possible ways）— 對應 Unity GameLogic.GetPossibleWays
// （來源 slot026_redridinghood/.../Script/GameLogic.cs:187-209，已逐行核對）。
//
// 演算法：ways = 各主輪「符號顆數」的乘積。關鍵是「顆數」而非「格數」——
//   一顆長條大符號（Megaways 風格，頂格 size>=2）不論垂直佔幾格，都只算「一顆」
//   （對應 Unity ReelSymbol.Symbols.Count：每顆符號一個物件，佔格數另存在 Size 欄位）。
// Tracker 頂輪：從主輪 index 1（TRACKER_OVERLAY_REELS[0]）起、連續「頂輪符號顆數」個主輪，
//   各多算「一顆」（Unity 為硬編碼 +1，不看頂輪符號的 Size）。
//
// 注意：本專案盤面採「固定網格 + size 標記」表示（見 model/board.ts），一顆長條符號 = 頂格(size>=2)
// ＋ 其下 (size-1) 個延續格(size=0)。因此「符號顆數」＝該欄中「非延續格」的數量，恰好等價於
// Unity 的 Symbols.Count。切勿改用 column.length（格數恆為 VISIBLE_ROWS，會算出與盤面無關的死值）。

import { TRACKER_OVERLAY_REELS } from '../constants'
import { isCovered } from '../model/board'
import type { Board, ReelColumn } from '../model/board'

/** 一欄的「符號顆數」：長條符號的頂格算一顆、被覆蓋的延續格(size=0)不算、一般格算一顆。 */
export function columnSymbolCount(column: ReelColumn): number {
  return column.reduce((count, cell) => (isCovered(cell) ? count : count + 1), 0)
}

/** 各主輪的「有效符號顆數」（含 Tracker 頂輪疊加輪 +1 的加成）。見檔頭說明。 */
function effectiveCountsPerReel(board: Board, tracker: ReelColumn): number[] {
  const perReel = board.map(columnSymbolCount)
  const trackerSymbols = columnSymbolCount(tracker)
  // 從主輪 index 1 起、連續 trackerSymbols 個主輪各 +1 顆（對應 Unity i>=1 && i<1+trackerSymbolsCount）。
  for (let k = 0; k < trackerSymbols; k++) {
    const reel = TRACKER_OVERLAY_REELS[k]
    if (reel !== undefined && perReel[reel] !== undefined) perReel[reel] += 1
  }
  return perReel
}

/**
 * 逐輪累積的組合方式數：running[r] = 主輪 0..r 的有效符號顆數乘積。
 * 供「每輪由左至右停下時，組合方式數逐格累乘上去」的呈現（對應真機轉輪停一輪、方式數就長一次）。
 * running 最後一項即等於 computePossibleWays 的總方式數。
 */
export function accumulateWaysByReel(board: Board, tracker: ReelColumn): number[] {
  const perReel = effectiveCountsPerReel(board, tracker)
  const running: number[] = []
  let product = 1
  for (const count of perReel) {
    product *= count
    running.push(product)
  }
  return running
}

/**
 * 盤面總可能組合方式數（= 各主輪符號顆數乘積，Tracker 頂輪疊加輪各多一顆）。見檔頭說明。
 * 空盤面或全空欄會使某輪顆數為 0，乘積即為 0（與 Unity 一致）。
 */
export function computePossibleWays(board: Board, tracker: ReelColumn): number {
  return effectiveCountsPerReel(board, tracker).reduce((product, count) => product * count, 1)
}
